import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ChevronRight } from 'lucide-react';
import {
  useCriarInventario, usePreviewInventario, useLocations, useProjects, useClients, useCustodians,
  useCategorias,
} from '../hooks';
import { useAreas } from '../../cadastros-organizacionais/hooks';
import type { InventoryScopeType, CreateInventoryPayload } from '../api';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import {
  SCOPE_LABELS,
  escopoExigeItem,
  mensagemItemObrigatorio,
  nomeDoItem,
  opcoesDoItem,
  rotuloDoItem,
  type EscopoFontes,
} from './inventarioEscopo';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

/**
 * Wizard 2 etapas: (1) nome + escopo + item do escopo + prazo;
 * (2) preview de quantidade + confirmação.
 *
 * O item do escopo é uma lista dinâmica (issue #463): o rótulo e as
 * opções mudam conforme o escopo e a seleção é limpa ao trocar de
 * escopo, para nunca carregar um id de um cadastro para outro.
 */
export function NovoInventarioWizard({ open, onClose, onCreated }: Props) {
  const criar = useCriarInventario();
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [scopeType, setScopeType] = useState<InventoryScopeType>('all');
  const [scopeRefId, setScopeRefId] = useState<string>('');
  const [deadline, setDeadline] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const locations = useLocations();
  const areas = useAreas({ active: 'true', limit: 200 });
  const projects = useProjects();
  const clients = useClients();
  const custodians = useCustodians({});
  const categories = useCategorias();

  // Cada escopo alimenta a segunda lista com o seu cadastro. Área =
  // área organizacional, que o backend expande para os centros de custo da
  // área e das subáreas.
  const fontes: EscopoFontes = {
    areas: (areas.data?.data ?? []).map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` })),
    localizacoes: (locations.data?.data ?? []).map((l) => ({ value: l.id, label: l.name })),
    responsaveis: (custodians.data?.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    clientes: (clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    projetos: (projects.data?.data ?? []).map((p) => ({ value: p.id, label: p.name })),
    categorias: (categories.data?.groups ?? []).flatMap((group) =>
      group.items.map((c) => ({ value: c.id, label: c.name })),
    ),
  };
  const itemOpcoes = opcoesDoItem(scopeType, fontes);
  const itemLabel = rotuloDoItem(scopeType);

  const preview = usePreviewInventario(
    scopeType,
    scopeType === 'all' ? undefined : scopeRefId || undefined,
  );

  function reset() {
    setStep(1); setName(''); setScopeType('all'); setScopeRefId(''); setDeadline(''); setError(null);
  }

  function handleNext(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (name.trim().length < 2) { setError('Nome precisa ter ao menos 2 caracteres.'); return; }
    const obrigatorio = mensagemItemObrigatorio(scopeType);
    if (obrigatorio && !scopeRefId) { setError(obrigatorio); return; }
    setStep(2);
  }

  async function handleCreate() {
    setError(null);
    try {
      const payload: CreateInventoryPayload = {
        name: name.trim(),
        scopeType,
        ...(scopeType !== 'all' ? { scopeRefId } : {}),
        ...(deadline ? { deadline } : {}),
      };
      const res = await criar.mutateAsync(payload);
      reset();
      onCreated(res.inventory.id);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao criar ação anual de inventário'));
    }
  }

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-semibold text-gray-900">
            Novo inventário — etapa {step} de 2
          </Dialog.Title>

          {step === 1 && (
            <form onSubmit={handleNext} className="mt-4 space-y-3">
              <TextInput
                label="Nome da ação anual de inventário *"
                required
                minLength={2}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <AutocompleteSelect
                label="Escopo *"
                options={(Object.entries(SCOPE_LABELS) as Array<[InventoryScopeType, string]>).map(([v, l]) => ({ value: v, label: l }))}
                value={scopeType}
                onChange={(v) => { setScopeType(v as InventoryScopeType); setScopeRefId(''); }}
              />
              {itemLabel && (
                <AutocompleteSelect
                  label={`${itemLabel} *`}
                  options={[
                    { value: '', label: 'Selecione...' },
                    ...itemOpcoes,
                  ]}
                  value={scopeRefId}
                  onChange={setScopeRefId}
                />
              )}
              <TextInput
                label="Prazo (opcional)"
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
              />
              {error && <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { reset(); onClose(); }}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Cancelar</button>
                <button type="submit"
                  className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700">
                  Prévia <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          )}

          {step === 2 && (
            <div className="mt-4 space-y-3">
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm text-blue-900"><strong>Nome:</strong> {name}</p>
                <p className="text-sm text-blue-900"><strong>Escopo:</strong> {SCOPE_LABELS[scopeType]}</p>
                {escopoExigeItem(scopeType) && (
                  <p className="text-sm text-blue-900">
                    <strong>{rotuloDoItem(scopeType)}:</strong> {nomeDoItem(scopeType, scopeRefId, fontes)}
                  </p>
                )}
                {deadline && <p className="text-sm text-blue-900"><strong>Prazo:</strong> {deadline}</p>}
                <p className="mt-2 text-lg font-semibold text-blue-900">
                  {preview.isLoading ? 'Calculando prévia...' :
                    preview.isError ? 'Falha ao calcular prévia' :
                    `${preview.data?.count ?? 0} ativo(s) serão incluídos no snapshot`}
                </p>
              </div>
              <p className="text-xs text-gray-500">
                Snapshot congela os ativos no momento da criação — ativos criados depois NÃO entram.
                Ativos baixados e soft-deletados também ficam de fora.
              </p>
              {error && <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div>}
              <div className="flex justify-between gap-2 pt-2">
                <button type="button" onClick={() => setStep(1)}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Voltar</button>
                <button type="button" onClick={handleCreate} disabled={criar.isPending}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                  {criar.isPending ? 'Criando...' : 'Criar'}
                </button>
              </div>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
